import { observer } from 'mobx-react-lite';
import FilePickerCommandBar from './FilePickerCommandBar';
import FilePickerPane from './FilePickerPane';
import FileProviderAccountsView from './Account/FileProviderAccountsView';
import FileProvidersPane from './FileProvidersPane';
import changeFilterOptionAndReloadItemsForSelectedProvider from '../actions/changeFilterOptionAndReloadItemsForSelectedProvider';
import closeFilePicker from '../actions/closeFilePicker';
import setShowFilterOptions from '../actions/setShowFilterOptions';
import showFilePickerAction from '../actions/showFilePicker';
import toggleFilePickerView from '../actions/toggleFilePickerView';
import { updateSupportedFilePickerActionsForProviderFiles } from '../actions/updateSupportedFilePickerActions';
import actionsPanel from '../actionsPanel';
import { getFilePickerStore } from '../store/filePickerStore';
import FilePickerAction from '../store/schema/FilePickerAction';
import FilePickerConfiguration from '../store/schema/FilePickerConfiguration';
import FilePickerPanelViewState from '../store/schema/FilePickerPanelViewState';
import FileProviderItemViewState from '../store/schema/FileProviderItemViewState';
import FilePickerResult, { SelectedFilesSource } from '../types/FilePickerResult';
import { getFileProviderInfo } from '../utils/fileProviderInfo';
import { Modal } from 'office-ui-fabric-react/lib/Modal';
import { logUsage } from 'owa-analytics';
import { FilePickerUserConfiguration } from 'owa-filepicker';
import { FilePickerViewType } from 'owa-filepicker';
import { ClientOnlyFileProviderType, FileSelectionFilter } from 'owa-fileprovider-store';
import { getFileProviders } from 'owa-fileprovider-store/lib/selectors/getFileProviders';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentItemsFilterBy from 'owa-service/lib/contract/AttachmentItemsFilterBy';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

interface FilePickerPanelProps {
    onFilesSelectedCallback: (result: FilePickerResult) => void;
    onCancelCallback: () => void;
    viewState: FilePickerPanelViewState;
    disableLocalComputerFilePicker?: boolean;
    maxAllowedLocalFilesSize?: number;
    userConfiguration?: FilePickerUserConfiguration;
    forcedAction?: FilePickerAction | null;
    supportsInsertLink: boolean;
}

const styles = require('./FilePickerPanelView.scss');
const classNames = require('classnames');

/* exported for testing */
export const FILEPICKER_DIV_ID = 'filePickerPanelView';
// This component is responsible for showing the attachment file picker including inline images.
const FilePickerPanelView = observer(function FilePickerPanelView(props: FilePickerPanelProps) {
    const filePickerResult = React.useRef<FilePickerResult>(null);
    const renderFilePickerContent = () => {
        const filterOptionsState = props.viewState.filePickerViewState.filterOptionsState;
        let providerFilterOptions: AttachmentItemsFilterBy[] = [];
        if (props.viewState.selectedFileProvider != null) {
            providerFilterOptions = getFileProviderInfo(props.viewState.selectedFileProvider.type)
                .filePickerFilterOptions;
        }
        const { supportedViews, currentView } = getSupportedAndCurrentViews();
        const showFileProviderInlineView = props.viewState.showFileProviderInlineView;
        return (
            <div className={styles.filePickerContent}>
                <FilePickerCommandBar
                    toggleSelectedFilePickerView={onFilePickerViewChanged}
                    onCloseFilePickerClicked={closeFilePickerPanel}
                    selectedFilePickerView={currentView}
                    supportedFilePickerViews={supportedViews}
                    filterOptions={providerFilterOptions}
                    selectedFilterOption={filterOptionsState.selectedFilterOption}
                    showFilterOptionsMenu={filterOptionsState.showFilterOptions}
                    onFilterIconClicked={onFilterClicked}
                    onFilterOptionClicked={onFilterOptionClicked}
                    onFilterMenuDismissed={onFilterMenuDismissed}
                    isProviderListView={!showFileProviderInlineView}
                />
                <div className={styles.content}>
                    <FileProvidersPane
                        className={classNames(styles.fileProvidersPane)}
                        fileSelectionFilter={props.viewState.fileSelectionFilter}
                        onLocalComputerFilesSelected={onLocalComputerFilesSelected}
                        filePickerPanelViewState={props.viewState}
                        selectedFileProvider={props.viewState.selectedFileProvider}
                        disableLocalComputerFilePicker={props.disableLocalComputerFilePicker}
                        showFileProviderInlineView={showFileProviderInlineView}
                    />
                    {!showFileProviderInlineView && (
                        <FilePickerPane
                            className={classNames(styles.filePickerPane)}
                            viewState={props.viewState}
                            onFilesSelected={onProviderFilesSelected}
                            maxAllowedLocalFilesSize={props.maxAllowedLocalFilesSize}
                            selectedView={currentView}
                            isTopLevelGroupsView={isTopLevelGroupsView()}
                        />
                    )}
                    {showFileProviderInlineView && (
                        <FileProviderAccountsView className={classNames(styles.filePickerPane)} />
                    )}
                </div>
            </div>
        );
    };
    const getSupportedAndCurrentViews = (): {
        supportedViews: FilePickerViewType[];
        currentView: FilePickerViewType;
    } => {
        if (isTopLevelGroupsView()) {
            return {
                supportedViews: [FilePickerViewType.Groups],
                currentView: FilePickerViewType.Groups,
            };
        } else {
            return {
                supportedViews: props.viewState.filePickerViewState.supportedViews,
                currentView: props.viewState.filePickerViewState.selectedView,
            };
        }
    };
    const isTopLevelGroupsView = () => {
        return (
            props.viewState.selectedFileProvider?.usesGroupsViewAtTopLevel &&
            props.viewState.filePickerViewState.selectedFolder === null
        );
    };
    const onProviderFilesSelected = (files: FileProviderItemViewState[]) => {
        if (props.viewState.filePickerViewState.searchViewState.isInSearchMode) {
            logUsage('FPSelectedFromSearch', [props.viewState.selectedFileProvider.type]);
        }
        const {
            supportsInsertLink,
            showActionsPanel: shouldShowActionsPanel,
        } = getFileProviderInfo(props.viewState.selectedFileProvider.type);
        filePickerResult.current = {
            files: files,
            source: SelectedFilesSource.ProviderFiles,
            action: null,
            supportsInsertLink: supportsInsertLink,
        };
        // If the data provider does not allow showing the action panel then we execute the providers default action
        if (!shouldShowActionsPanel) {
            executeFileProviderDefaultAction(props.viewState.selectedFileProvider.type);
            return;
        }
        showActionsPanel(files, SelectedFilesSource.ProviderFiles, props.supportsInsertLink);
    };
    const executeFileProviderDefaultAction = (dataProviderType: AttachmentDataProviderType) => {
        switch (dataProviderType) {
            case AttachmentDataProviderType.Mailbox:
                onActionSelected(FilePickerAction.AttachAndShare);
                break;
            case AttachmentDataProviderType.Facebook:
                onActionSelected(FilePickerAction.Attach);
                break;
            case AttachmentDataProviderType.MailMessage:
                onActionSelected(FilePickerAction.Attach);
                break;
            default:
                throw new Error(
                    'This provider does not have a current default action assigned to it.'
                );
        }
    };
    const onLocalComputerFilesSelected = (files: File[], filter: FileSelectionFilter) => {
        if (!files || files.length === 0) {
            return;
        }
        filePickerResult.current = {
            files: files,
            source: SelectedFilesSource.LocalComputerFiles,
            action: null,
            supportsInsertLink: false,
        };
        showActionsPanel(files, SelectedFilesSource.LocalComputerFiles, false); // Insert as link is not supported for local files
    };
    const onFilePickerViewChanged = () => {
        const {
            viewState: { filePickerViewState, selectedFileProvider },
        } = props;
        const onViewTypeChanged = props.userConfiguration?.preferredViewType
            ? props.userConfiguration.preferredViewType.valueSetter
            : null;
        toggleFilePickerView(selectedFileProvider, filePickerViewState, onViewTypeChanged);
    };
    const showActionsPanel = (
        files: File[] | FileProviderItemViewState[],
        selectedFileSource: SelectedFilesSource,
        supportsInsertLink: boolean
    ) => {
        if (!!props.forcedAction) {
            onActionSelected(props.forcedAction);
            return;
        }

        const { fileSelectionFilter, selectedFileProvider } = props.viewState;
        const fileProvidersViewState = getFileProviders();
        if (fileSelectionFilter === FileSelectionFilter.Images) {
            // If we already know this is the inline image picker we don't need to show the actions panel
            onActionSelected(FilePickerAction.Insert);
            return;
        }
        if (fileProvidersViewState.length === 0) {
            // If we have not loaded the current file providers then skip actions panel and
            // directly attach
            onActionSelected(FilePickerAction.Attach);
            return;
        }
        const isGroupsFileProvider: boolean =
            props.viewState.selectedFileProvider &&
            props.viewState.selectedFileProvider.viewType === ClientOnlyFileProviderType.Groups;
        actionsPanel(
            files,
            props.maxAllowedLocalFilesSize,
            selectedFileSource === SelectedFilesSource.ProviderFiles
                ? selectedFileProvider.type
                : null,
            isGroupsFileProvider,
            files.length > 1,
            selectedFileSource,
            props.userConfiguration,
            props.viewState.filePickerActionsViewState.uploadConfiguration.composeUniqueState,
            supportsInsertLink,
            { filePickerPanelViewState: props.viewState }
        ).then(onActionSelected, onActionPanelCancelled);
    };
    const onActionPanelCancelled = () => {
        resetSupportedActionsForSelectedProviderFiles();
        filePickerResult.current = null;
    };
    const resetSupportedActionsForSelectedProviderFiles = () => {
        const { selectedFileProvider, filePickerViewState } = props.viewState;
        updateSupportedFilePickerActionsForProviderFiles(
            filePickerViewState.selectedFileProviderItems,
            props.maxAllowedLocalFilesSize,
            selectedFileProvider,
            props.viewState,
            props.supportsInsertLink
        );
    };
    const onActionSelected = (action: FilePickerAction) => {
        filePickerResult.current.action = action;
        closeFilePickerPanel();
    };
    const closeFilePickerPanel = () => {
        closeFilePicker(props.maxAllowedLocalFilesSize, props.viewState);
    };
    const onPanelDismissed = () => {
        // We need to make the callbacks only when the animation is complete so the focus can be changed back
        if (!filePickerResult.current) {
            logUsage('FPDismissProviderType', [props.viewState.selectedFileProvider.model.Type]);
            props.onCancelCallback();
        }
        props.onFilesSelectedCallback(filePickerResult.current);
        filePickerResult.current = null;
    };
    const onFilterClicked = () => {
        setShowFilterOptions(
            props.viewState.filePickerViewState.filterOptionsState,
            !props.viewState.filePickerViewState.filterOptionsState.showFilterOptions
        );
    };
    const onFilterMenuDismissed = () => {
        setShowFilterOptions(props.viewState.filePickerViewState.filterOptionsState, false);
    };
    const onFilterOptionClicked = (selectedFilter: AttachmentItemsFilterBy) => {
        changeFilterOptionAndReloadItemsForSelectedProvider(props.viewState, selectedFilter);
    };
    return (
        <Modal
            isOpen={props.viewState.isOpen}
            onDismiss={closeFilePickerPanel}
            onDismissed={onPanelDismissed}>
            <div className={styles.modalContainer}>{renderFilePickerContent()}</div>
        </Modal>
    );
});

/**
 * The showFilePicker is responsible for creating an element and then rendering the FilePicker in it.
 * The reason to do that is to make the file picker component simpler from a user perspective so that
 * they don't have to worry about creating the component themselves and maintaining show/close lifecycle.
 * Instead they create the file picker and once user selects files they get the files back.
 */
export default function showFilePickerPanel(
    resolve: (result: FilePickerResult) => void,
    reject: () => void,
    filePickerConfiguration: FilePickerConfiguration
): void {
    const {
        userConfiguration,
        fileSelectionFilter,
        uploadConfiguration,
        maxAllowedLocalFilesSize,
        disableLocalComputerFilePicker,
        forcedAction,
        targetWindow,
        supportsInsertLink,
    } = filePickerConfiguration;
    const document = (targetWindow || window).document;
    let filePickerDiv = document.getElementById(FILEPICKER_DIV_ID);
    if (!filePickerDiv) {
        // As we can have only one file picker open at a time so we only create the
        // DOM element once and reuse that.
        filePickerDiv = document.createElement('div');
        filePickerDiv.id = FILEPICKER_DIV_ID;
        document.body.appendChild(filePickerDiv);
    }

    ReactDOM.render(
        <React.StrictMode>
            <FilePickerPanelView
                onFilesSelectedCallback={resolve}
                onCancelCallback={reject}
                viewState={getFilePickerStore().viewState}
                disableLocalComputerFilePicker={disableLocalComputerFilePicker}
                maxAllowedLocalFilesSize={maxAllowedLocalFilesSize}
                userConfiguration={userConfiguration}
                forcedAction={forcedAction}
                supportsInsertLink={supportsInsertLink}
            />
        </React.StrictMode>,
        filePickerDiv
    );

    const viewType = userConfiguration?.preferredViewType.value;
    showFilePickerAction(
        fileSelectionFilter,
        viewType,
        getFilePickerStore().viewState,
        uploadConfiguration
    );
}
